import React from 'react';
import { withProperty } from '../PropertyProvider';
import {
	withMarketplaceServiceActivityProxy,
	withOfferService,
	withPropertyService,
} from '../../../service-container';
import { withSettings } from '../../../shared/SettingProvider/SettingProvider';
import { withTheme } from '../../../shared/ThemeProvider';
import FormSuccessMessages from '../../../shared/Forms/Messages/FormSuccessMessages';
import FormWarningMessages from '../../../shared/Forms/Messages/FormWarningMessages';
import { getCategoryValue } from '../../../Marketplace/helpers';
import captureError from '../../../utils/captureError';
import Loading from '../../../shared/Loading';
import paths from '../../../config/local/paths';
import {
	clearPreferredVendorCache,
	getCategoryLabel,
	getCombinedCategoryState,
	getPreferredVendorCacheKey,
	getStaticHomeProCategories,
	getVendorCategoryCacheKey,
	getVendorFilterCategories,
	preferredVendorRequestCache,
	preferredVendorResponseCache,
	vendorCategoryRequestCache,
	vendorCategoryResponseCache,
} from './PropertyHomePros.helpers';
import {
	CategoryRow,
	PreferredVendorSection,
	SelectedCategorySection,
	VendorCategoryFilter,
} from './PropertyHomeProsSections';

const OTHER_CATEGORY_LABEL = 'Other Category';
const NO_VENDORS_FOUND_WARNING =
	'We could not find any vendors in the system associated with the category you selected.';
const EMPTY_CATEGORY_STATE = {
	isLoading: false,
	localOfferList: [],
	offerList: [],
	tenantOfferList: [],
};

/**
 * @typedef {{ force?: boolean }} LoadCategoryOptions
 * @typedef {() => Promise<void>} CategorySaveAction
 */

/**
 * @param {any[]} vendors
 * @returns {any[]}
 */
function mapPreferredVendors(vendors) {
	return vendors.map((vendor) => ({
		...vendor,
		headline: vendor.name,
		logoUrl: null,
		websiteUrl: vendor.websiteUrl,
	}));
}

/**
 * @param {any[]} vendors
 * @returns {string[]}
 */
function getPreferredVendorCategories(vendors) {
	return Array.from(
		new Set(
			vendors.map(
				(vendor) => getCategoryValue(vendor.category) ?? vendor.category
			)
		)
	);
}

/**
 * @param {string[]} selectedCategories
 * @param {string} vendorCategoryFilter
 * @returns {string[]}
 */
function getVisibleVendorCategories(selectedCategories, vendorCategoryFilter) {
	if (!vendorCategoryFilter) {
		return selectedCategories;
	}

	return selectedCategories.includes(vendorCategoryFilter)
		? [vendorCategoryFilter]
		: [];
}

/**
 * @param {Record<string, { isLoading: boolean, localOfferList: any[], offerList: any[], tenantOfferList: any[] }>} categoryResults
 * @param {string[]} selectedCategories
 * @param {string} vendorCategoryFilter
 * @returns {{ isLoading: boolean, localOfferList: any[], offerList: any[], tenantOfferList: any[] }}
 */
function getSelectedCategoryState(
	categoryResults,
	selectedCategories,
	vendorCategoryFilter
) {
	if (!vendorCategoryFilter) {
		return getCombinedCategoryState(categoryResults, selectedCategories);
	}

	if (!selectedCategories.includes(vendorCategoryFilter)) {
		return EMPTY_CATEGORY_STATE;
	}

	return categoryResults[vendorCategoryFilter] ?? EMPTY_CATEGORY_STATE;
}

/**
 * @param {any[]} currentVendors
 * @param {any} preferredVendor
 * @returns {any[]}
 */
function addPreferredVendorOnce(currentVendors, preferredVendor) {
	if (currentVendors.some((vendor) => vendor.id === preferredVendor.id)) {
		return currentVendors;
	}

	return [preferredVendor, ...currentVendors];
}

/**
 * @param {PromiseSettledResult<any>[]} results
 * @returns {boolean}
 */
function areVendorCategoryResultsCacheable(results) {
	return results.every((result) => result.status === 'fulfilled');
}

/**
 * @param {string[]} currentCategories
 * @param {string} category
 * @returns {string[]}
 */
function removeSelectedCategory(currentCategories, category) {
	return currentCategories.filter(
		(currentCategory) => currentCategory !== category
	);
}

/**
 * @param {string} left
 * @param {string} right
 * @returns {number}
 */
function compareCategoryLabels(left, right) {
	return getCategoryLabel(left).localeCompare(getCategoryLabel(right));
}

/**
 * @param {string[]} categories
 * @returns {string[]}
 */
function normalizeCategoryList(categories) {
	return Array.from(
		new Set(
			(categories ?? [])
				.map((category) => getCategoryValue(category) ?? category)
				.filter(Boolean)
		)
	).sort(compareCategoryLabels);
}

/**
 * @param {string[]} categories
 * @param {string} category
 * @returns {string[]}
 */
function addPendingCategory(categories, category) {
	if (categories.includes(category)) {
		return categories;
	}

	return [...categories, category];
}

/**
 * @param {any[]} vendors
 * @param {string} category
 * @returns {any[]}
 */
function filterVendorsByCategory(vendors, category) {
	return vendors.filter(
		(vendor) =>
			(getCategoryValue(vendor.category) ?? vendor.category) === category
	);
}

/**
 * @param {string[]} categories
 * @param {string} category
 * @returns {string[]}
 */
function removePendingCategory(categories, category) {
	return categories.filter((pendingCategory) => pendingCategory !== category);
}

/**
 * @param {string} currentCategory
 * @param {string} removedCategory
 * @returns {string}
 */
function getCategoryFilterAfterRemove(currentCategory, removedCategory) {
	return currentCategory === removedCategory ? '' : currentCategory;
}

/**
 * @param {any} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line complexity
function PropertyHomePros(props) {
	const address = props.property?.selectedProperty?.address || {};
	const propertyId = props.match?.params?.propertyId ?? '';
	const isWaitingForPropertyDetails = React.useMemo(
		() =>
			Boolean(propertyId) &&
			props.property?.selectedProperty?.id === propertyId &&
			props.property?.selectedPropertyDetails === null,
		[
			propertyId,
			props.property?.selectedProperty?.id,
			props.property?.selectedPropertyDetails,
		]
	);
	const persistedSelectedCategories = React.useMemo(() => {
		if (props.property?.selectedProperty?.id !== propertyId) {
			return [];
		}

		return normalizeCategoryList(
			props.property?.selectedPropertyDetails?.preferredCategories ?? []
		);
	}, [
		propertyId,
		props.property?.selectedProperty?.id,
		props.property?.selectedPropertyDetails?.preferredCategories,
	]);
	const [selectedCategories, setSelectedCategories] = React.useState(
		/** @type {string[]} */ ([])
	);
	const [removedCategories, setRemovedCategories] = React.useState(
		/** @type {string[]} */ ([])
	);
	const [vendorCategoryFilter, setVendorCategoryFilter] = React.useState('');
	const [preferredVendors, setPreferredVendors] = React.useState(
		/** @type {any[]} */ ([])
	);
	const [isRestoringCategories, setIsRestoringCategories] =
		React.useState(false);
	const [preferredVendorCategories, setPreferredVendorCategories] =
		React.useState(/** @type {string[]} */ ([]));
	const [isPreferredVendorsLoading, setIsPreferredVendorsLoading] =
		React.useState(false);
	const [preferredVendorMessage, setPreferredVendorMessage] = React.useState(
		/** @type {string[]} */ ([])
	);
	const [warningMessages, setWarningMessages] = React.useState(
		/** @type {string[]} */ ([])
	);
	const [pendingCategoryActions, setPendingCategoryActions] = React.useState(
		/** @type {string[]} */ ([])
	);
	const [categoryResults, setCategoryResults] = React.useState(
		/** @type {Record<string, { isLoading: boolean, localOfferList: any[], offerList: any[], tenantOfferList: any[] }>} */ ({})
	);
	const requestIdsRef = React.useRef(
		/** @type {Record<string, number>} */ ({})
	);
	const pendingCategoryActionsRef = React.useRef(
		/** @type {string[]} */ ([])
	);
	const categorySaveQueueRef = React.useRef(
		/** @type {Promise<void>} */ (Promise.resolve())
	);
	const preferredVendorRequestIdRef = React.useRef(0);
	const selectedCategoriesRef = React.useRef(selectedCategories);
	const restoredCategoriesRef = React.useRef(
		/** @type {string | null} */ (null)
	);
	const displaySelectedCategories = React.useMemo(
		() =>
			normalizeCategoryList([
				...persistedSelectedCategories.filter(
					(category) => !removedCategories.includes(category)
				),
				...selectedCategories,
			]),
		[persistedSelectedCategories, removedCategories, selectedCategories]
	);

	const categories = React.useMemo(() => getStaticHomeProCategories(), []);
	const vendorFilterCategories = React.useMemo(
		() =>
			getVendorFilterCategories(
				preferredVendorCategories,
				displaySelectedCategories
			),
		[displaySelectedCategories, preferredVendorCategories]
	);
	const visibleVendorCategories = React.useMemo(
		() =>
			getVisibleVendorCategories(
				displaySelectedCategories,
				vendorCategoryFilter
			),
		[displaySelectedCategories, vendorCategoryFilter]
	);
	const selectedCategoryState = React.useMemo(
		() =>
			getSelectedCategoryState(
				categoryResults,
				displaySelectedCategories,
				vendorCategoryFilter
			),
		[categoryResults, displaySelectedCategories, vendorCategoryFilter]
	);
	const visiblePreferredVendors = React.useMemo(() => {
		if (!vendorCategoryFilter) {
			return preferredVendors;
		}

		return filterVendorsByCategory(preferredVendors, vendorCategoryFilter);
	}, [preferredVendors, vendorCategoryFilter]);

	const isVendorListLoading = React.useMemo(
		() =>
			isRestoringCategories ||
			isWaitingForPropertyDetails ||
			isPreferredVendorsLoading ||
			pendingCategoryActions.length > 0 ||
			persistedSelectedCategories.some(
				(category) =>
					!removedCategories.includes(category) &&
					categoryResults[category]?.isLoading
			),
		[
			categoryResults,
			isPreferredVendorsLoading,
			isRestoringCategories,
			isWaitingForPropertyDetails,
			pendingCategoryActions.length,
			persistedSelectedCategories,
			removedCategories,
		]
	);
	const isCategoryListLoading =
		isWaitingForPropertyDetails || isRestoringCategories;

	React.useEffect(() => {
		selectedCategoriesRef.current = selectedCategories;
	}, [selectedCategories]);

	const startCategoryAction = React.useCallback(
		/**
		 * @param {string} category
		 * @returns {boolean}
		 */
		(category) => {
			if (pendingCategoryActionsRef.current.includes(category)) {
				return false;
			}

			pendingCategoryActionsRef.current = addPendingCategory(
				pendingCategoryActionsRef.current,
				category
			);
			setPendingCategoryActions(pendingCategoryActionsRef.current);

			return true;
		},
		[]
	);

	const finishCategoryAction = React.useCallback(
		/**
		 * @param {string} category
		 * @returns {void}
		 */
		(category) => {
			pendingCategoryActionsRef.current = removePendingCategory(
				pendingCategoryActionsRef.current,
				category
			);
			setPendingCategoryActions(pendingCategoryActionsRef.current);
		},
		[]
	);

	const enqueueCategorySave = React.useCallback(
		/**
		 * @param {CategorySaveAction} saveCategory
		 * @returns {Promise<void>}
		 */
		(saveCategory) => {
			const nextSave = categorySaveQueueRef.current
				.catch(() => undefined)
				.then(saveCategory);

			categorySaveQueueRef.current = nextSave.catch(() => undefined);

			return nextSave;
		},
		[]
	);

	React.useEffect(() => {
		restoredCategoriesRef.current = null;
		pendingCategoryActionsRef.current = [];
		categorySaveQueueRef.current = Promise.resolve();
		setPreferredVendorMessage([]);
		setWarningMessages([]);
		setSelectedCategories([]);
		setRemovedCategories([]);
		setVendorCategoryFilter('');
		setPendingCategoryActions([]);
		setCategoryResults({});
		setIsRestoringCategories(Boolean(propertyId));
	}, [propertyId]);

	React.useEffect(() => {
		let isMounted = true;
		const category = '';
		const requestId = preferredVendorRequestIdRef.current + 1;

		preferredVendorRequestIdRef.current = requestId;

		if (!propertyId) {
			return undefined;
		}

		// eslint-disable-next-line complexity
		(async () => {
			setIsPreferredVendorsLoading(true);

			try {
				const cacheKey = getPreferredVendorCacheKey(
					propertyId,
					category
				);

				if (preferredVendorResponseCache.has(cacheKey)) {
					const cachedVendors =
						preferredVendorResponseCache.get(cacheKey) ?? [];

					if (
						!isMounted ||
						preferredVendorRequestIdRef.current !== requestId
					) {
						return;
					}

					setPreferredVendors(cachedVendors);

					if (!category) {
						setPreferredVendorCategories(
							getPreferredVendorCategories(cachedVendors)
						);
					}

					setIsPreferredVendorsLoading(false);
					return;
				}

				const pendingRequest =
					preferredVendorRequestCache.get(cacheKey) ??
					props.offerService.getPreferredVendors(propertyId);

				preferredVendorRequestCache.set(cacheKey, pendingRequest);
				const vendors = await pendingRequest;
				const mappedVendors = mapPreferredVendors(vendors);

				preferredVendorResponseCache.set(cacheKey, mappedVendors);
				preferredVendorRequestCache.delete(cacheKey);

				if (
					!isMounted ||
					preferredVendorRequestIdRef.current !== requestId
				) {
					return;
				}

				setPreferredVendors(mappedVendors);

				if (!category) {
					setPreferredVendorCategories(
						getPreferredVendorCategories(mappedVendors)
					);
				}

				setIsPreferredVendorsLoading(false);
			} catch (error) {
				preferredVendorRequestCache.delete(
					getPreferredVendorCacheKey(propertyId, category)
				);

				if (
					!isMounted ||
					preferredVendorRequestIdRef.current !== requestId
				) {
					return;
				}

				setIsPreferredVendorsLoading(false);
				captureError(error);
			}
		})();

		return () => {
			isMounted = false;
		};
	}, [propertyId, props.offerService]);

	React.useEffect(() => {
		const preferredVendor = props.location?.state?.preferredVendor;
		const message = props.location?.state?.message;

		if (!preferredVendor && !message) {
			return;
		}

		if (message?.length) {
			setPreferredVendorMessage(message);
		}

		if (preferredVendor) {
			setPreferredVendors((currentVendors) => {
				const nextVendors = addPreferredVendorOnce(
					currentVendors,
					preferredVendor
				);

				if (propertyId) {
					clearPreferredVendorCache(propertyId);
					preferredVendorResponseCache.set(
						getPreferredVendorCacheKey(propertyId, ''),
						nextVendors
					);
				}

				return nextVendors;
			});
			setPreferredVendorCategories((currentCategories) => {
				const category =
					getCategoryValue(preferredVendor.category) ??
					preferredVendor.category;

				if (!category || currentCategories.includes(category)) {
					return currentCategories;
				}

				return [...currentCategories, category].sort(
					compareCategoryLabels
				);
			});
		}

		props.history.replace({
			...props.location,
			state: undefined,
		});
	}, [propertyId, props.history, props.location]);

	const loadCategories = React.useCallback(
		/**
		 * @param {string[]} categoryList
		 * @returns {Promise<void>}
		 */
		async (categoryList) => {
			const normalizedCategories = normalizeCategoryList(categoryList);

			if (!normalizedCategories.length) {
				return;
			}

			setCategoryResults((currentResults) => {
				const nextResults = { ...currentResults };

				for (const category of normalizedCategories) {
					nextResults[category] = {
						isLoading: true,
						localOfferList: [],
						offerList: [],
						tenantOfferList: [],
					};
				}

				return nextResults;
			});

			const requestIds = normalizedCategories.reduce(
				/**
				 * @param {Record<string, number>} ids
				 * @param {string} category
				 * @returns {Record<string, number>}
				 */
				(ids, category) => {
					const requestId =
						(requestIdsRef.current[category] ?? 0) + 1;

					requestIdsRef.current[category] = requestId;
					ids[category] = requestId;

					return ids;
				},
				{}
			);

			const postalCode = address?.postalCode ?? '';
			const cacheKey = getVendorCategoryCacheKey(
				propertyId,
				postalCode,
				normalizedCategories
			);
			const [offerResult, tenantResult, localResult] =
				vendorCategoryResponseCache.get(cacheKey) ??
				(await (vendorCategoryRequestCache.get(cacheKey) ??
					(async () => {
						const pendingRequest = Promise.allSettled([
							props.marketplaceService.getVendors(
								normalizedCategories,
								postalCode,
								1
							),
							props.marketplaceService.getVendors(
								normalizedCategories,
								'',
								1,
								'',
								{
									limit: 50,
									relatedType: 'tenant',
								}
							),
							props.marketplaceService.getVendors(
								normalizedCategories,
								'',
								1,
								'',
								{
									limit: 50,
									relatedType: 'local',
								}
							),
						]);

						vendorCategoryRequestCache.set(
							cacheKey,
							pendingRequest
						);

						const results = await pendingRequest;
						if (areVendorCategoryResultsCacheable(results)) {
							vendorCategoryResponseCache.set(cacheKey, results);
						}
						vendorCategoryRequestCache.delete(cacheKey);

						return results;
					})()));

			for (const result of [offerResult, tenantResult, localResult]) {
				if (result.status === 'rejected') {
					captureError(result.reason);
				}
			}

			setCategoryResults((currentResults) => {
				const nextResults = { ...currentResults };

				for (const category of normalizedCategories) {
					if (
						requestIdsRef.current[category] !== requestIds[category]
					) {
						continue;
					}

					const currentResult =
						currentResults[category] ?? EMPTY_CATEGORY_STATE;

					nextResults[category] = {
						isLoading: false,
						localOfferList:
							localResult.status === 'fulfilled'
								? filterVendorsByCategory(
										localResult.value.vendors,
										category
								  )
								: currentResult.localOfferList,
						offerList:
							offerResult.status === 'fulfilled'
								? filterVendorsByCategory(
										offerResult.value.vendors,
										category
								  )
								: currentResult.offerList,
						tenantOfferList:
							tenantResult.status === 'fulfilled'
								? filterVendorsByCategory(
										tenantResult.value.vendors,
										category
								  )
								: currentResult.tenantOfferList,
					};
				}

				return nextResults;
			});
		},
		[address?.postalCode, props.marketplaceService]
	);

	React.useEffect(() => {
		if (!propertyId) {
			setIsRestoringCategories(false);
			return;
		}

		if (isWaitingForPropertyDetails) {
			setIsRestoringCategories(true);
			return;
		}

		if (restoredCategoriesRef.current !== null) {
			setIsRestoringCategories(false);
			return;
		}

		const persistedKey = persistedSelectedCategories.join(',');

		restoredCategoriesRef.current = persistedKey;
		setSelectedCategories(persistedSelectedCategories);
		setRemovedCategories([]);
		setCategoryResults((currentResults) => {
			const nextResults = { ...currentResults };

			for (const category of persistedSelectedCategories) {
				nextResults[category] ??= EMPTY_CATEGORY_STATE;
			}

			return nextResults;
		});

		loadCategories(persistedSelectedCategories);
		setIsRestoringCategories(false);
	}, [
		isWaitingForPropertyDetails,
		loadCategories,
		propertyId,
		persistedSelectedCategories,
	]);

	const handleAddCategory = React.useCallback(
		/**
		 * @param {string} category
		 * @returns {Promise<void>}
		 */
		async (category) => {
			const normalizedCategory = getCategoryValue(category) ?? category;
			const persistedCategories = normalizeCategoryList(
				props.property?.selectedPropertyDetails?.preferredCategories ??
					[]
			);

			if (
				categoryResults[normalizedCategory]?.isLoading ||
				displaySelectedCategories.includes(normalizedCategory) ||
				persistedCategories.includes(normalizedCategory)
			) {
				return;
			}

			if (!startCategoryAction(normalizedCategory)) {
				return;
			}

			setPreferredVendorMessage([]);
			setWarningMessages([]);
			setRemovedCategories((currentCategories) =>
				removeSelectedCategory(currentCategories, normalizedCategory)
			);
			setCategoryResults((currentResults) => ({
				...currentResults,
				[normalizedCategory]: {
					isLoading: true,
					localOfferList: [],
					offerList: [],
					tenantOfferList: [],
				},
			}));

			const requestId =
				(requestIdsRef.current[normalizedCategory] ?? 0) + 1;
			requestIdsRef.current[normalizedCategory] = requestId;

			try {
				const postalCode = address?.postalCode ?? '';
				const [offerResult, tenantResult, localResult] =
					await Promise.all([
						props.marketplaceService.getVendors(
							normalizedCategory,
							postalCode,
							1
						),
						props.marketplaceService.getVendors(
							normalizedCategory,
							'',
							1,
							'',
							{
								limit: 50,
								relatedType: 'tenant',
							}
						),
						props.marketplaceService.getVendors(
							normalizedCategory,
							'',
							1,
							'',
							{
								limit: 50,
								relatedType: 'local',
							}
						),
					]);

				if (requestIdsRef.current[normalizedCategory] !== requestId) {
					return;
				}

				const hasVendors =
					offerResult.vendors.length > 0 ||
					tenantResult.vendors.length > 0 ||
					localResult.vendors.length > 0;

				if (!hasVendors) {
					setWarningMessages([NO_VENDORS_FOUND_WARNING]);
					setCategoryResults((currentResults) => ({
						...currentResults,
						[normalizedCategory]: EMPTY_CATEGORY_STATE,
					}));

					return;
				}

				await enqueueCategorySave(() =>
					props.propertyService.updatePreferredCategories(
						propertyId,
						[normalizedCategory],
						'merge'
					)
				);

				setSelectedCategories((currentCategories) => {
					const nextCategories = normalizeCategoryList([
						...currentCategories,
						normalizedCategory,
					]);

					selectedCategoriesRef.current = nextCategories;

					return nextCategories;
				});
				setWarningMessages([]);
				setCategoryResults((currentResults) => ({
					...currentResults,
					[normalizedCategory]: {
						isLoading: false,
						localOfferList: localResult.vendors,
						offerList: offerResult.vendors,
						tenantOfferList: tenantResult.vendors,
					},
				}));
				props.property.refreshSelectedPropertyDetails();
			} catch (error) {
				captureError(error);
				setCategoryResults((currentResults) => ({
					...currentResults,
					[normalizedCategory]: EMPTY_CATEGORY_STATE,
				}));
			} finally {
				finishCategoryAction(normalizedCategory);
			}
		},
		[
			address?.postalCode,
			categoryResults,
			displaySelectedCategories,
			enqueueCategorySave,
			finishCategoryAction,
			propertyId,
			props.marketplaceService,
			props.property,
			props.propertyService,
			startCategoryAction,
		]
	);

	const handleRemoveCategory = React.useCallback(
		async (/** @type {string} */ category) => {
			if (!startCategoryAction(category)) {
				return;
			}

			try {
				setPreferredVendorMessage([]);
				setWarningMessages([]);
				requestIdsRef.current[category] =
					(requestIdsRef.current[category] ?? 0) + 1;
				const persistedCategories = normalizeCategoryList(
					props.property?.selectedPropertyDetails
						?.preferredCategories ?? []
				);
				const nextCategories = removeSelectedCategory(
					normalizeCategoryList([
						...persistedCategories,
						...selectedCategoriesRef.current,
					]),
					category
				);

				setVendorCategoryFilter((currentCategory) =>
					getCategoryFilterAfterRemove(currentCategory, category)
				);
				setRemovedCategories((currentCategories) =>
					addPendingCategory(currentCategories, category)
				);
				setSelectedCategories(() => {
					selectedCategoriesRef.current = nextCategories;

					return nextCategories;
				});
				setCategoryResults((currentResults) => ({
					...currentResults,
					[category]: EMPTY_CATEGORY_STATE,
				}));

				await enqueueCategorySave(() =>
					props.propertyService.updatePreferredCategories(
						propertyId,
						[category],
						'remove'
					)
				);
				props.property.refreshSelectedPropertyDetails();
			} catch (error) {
				captureError(error);
			} finally {
				finishCategoryAction(category);
			}
		},
		[
			enqueueCategorySave,
			finishCategoryAction,
			propertyId,
			props.property,
			props.propertyService,
			startCategoryAction,
		]
	);
	/* eslint-enable complexity */

	const handleVendorCategoryFilterChange = React.useCallback(
		/**
		 * @param {React.ChangeEvent<HTMLSelectElement>} event
		 * @returns {void}
		 */
		(event) => {
			const category = event?.target?.value ?? '';

			setPreferredVendorMessage([]);
			setWarningMessages([]);
			setVendorCategoryFilter(category);
		},
		[]
	);

	const otherCategoryLocation = {
		pathname:
			paths.app.homeowner.properties.scoped.addPreferredVendor.replace(
				':propertyId',
				propertyId
			),
		state: {
			category:
				vendorCategoryFilter ||
				displaySelectedCategories[0] ||
				categories[0] ||
				'',
		},
	};

	return (
		<section className="app-block-list">
			{preferredVendorMessage.length > 0 ? (
				<FormSuccessMessages messages={preferredVendorMessage} />
			) : null}
			{warningMessages.length > 0 ? (
				<FormWarningMessages messages={warningMessages} />
			) : null}
			<header className="mb-6">
				<h2 className="h-ms-3 mb-2">{'My Home Pros'}</h2>
				<p className="text-grey-700 mb-0">
					{'Recommended vendor categories for this property.'}
				</p>
			</header>
			<section className="max-w-4xl relative">
				{isCategoryListLoading ? (
					<section className="absolute inset-0 z-10 flex items-start justify-center bg-white bg-opacity-70 pt-8">
						<Loading />
					</section>
				) : null}
				<section
					className={
						isCategoryListLoading
							? 'pointer-events-none opacity-60'
							: ''
					}
				>
					{/* eslint-disable-next-line complexity */}
					{categories.map((category) => {
						const isLoading =
							categoryResults[category]?.isLoading ?? false;
						const isSelected =
							displaySelectedCategories.includes(category);
						const isPendingAction =
							pendingCategoryActions.includes(category);

						return (
							<CategoryRow
								addDisabled={
									isLoading || isSelected || isPendingAction
								}
								key={category}
								label={getCategoryLabel(category)}
								onAdd={() => handleAddCategory(category)}
								onRemove={() => handleRemoveCategory(category)}
								removeDisabled={
									!isSelected || isLoading || isPendingAction
								}
							/>
						);
					})}
					<CategoryRow
						label={OTHER_CATEGORY_LABEL}
						onAdd={() => props.history.push(otherCategoryLocation)}
						showRemove={false}
					/>
				</section>
			</section>
			<section className="max-w-6xl relative">
				<VendorCategoryFilter
					categories={vendorFilterCategories}
					category={vendorCategoryFilter}
					onChange={handleVendorCategoryFilterChange}
				/>
				{isVendorListLoading ? (
					<section className="absolute inset-0 z-10 flex items-start justify-center bg-white bg-opacity-70 pt-8">
						<Loading />
					</section>
				) : null}
				<section
					className={
						isVendorListLoading
							? 'pointer-events-none opacity-60'
							: ''
					}
				>
					{visibleVendorCategories.length > 0 ? (
						<SelectedCategorySection
							address={address}
							categories={visibleVendorCategories}
							key={visibleVendorCategories.join(',')}
							settings={props.settings}
							state={selectedCategoryState}
							theme={props.theme}
						/>
					) : null}
					<PreferredVendorSection vendors={visiblePreferredVendors} />
				</section>
			</section>
		</section>
	);
}

export default withMarketplaceServiceActivityProxy(
	// @ts-ignore -- Existing service HOC typings do not preserve injected props.
	withOfferService(
		withPropertyService(
			withTheme(withSettings(withProperty(PropertyHomePros)))
		)
	)
);
